"use strict";

const APPEARANCE_SCHEMA_VERSION = 1;
const PALETTE_VERSION = 1;
const UNIT_ACCENT_SEED = "#745FF2";

const ACCENT_ROLE_NAMES = Object.freeze([
  "accent-primary",
  "accent-emphasis",
  "accent-selection",
  "accent-surface",
  "accent-surface-subtle",
  "text-on-accent",
  "text-on-accent-selection",
  "text-on-accent-subtle",
]);

const ORG_IDENTITY_ROLE_NAMES = Object.freeze([
  "org-identity-accent",
  "org-identity-surface",
  "org-identity-on-surface",
]);

const THEME_FOUNDATIONS = Object.freeze({
  light: Object.freeze({
    canvas: "#F0F0F5",
    lightForeground: "#FFFFFF",
    darkForeground: "#050506",
    selectionSeedWeight: 0.48,
    surfaceSeedWeight: 0.26,
    subtleSeedWeight: 0.14,
    identitySurfaceSeedWeight: 0.16,
  }),
  dark: Object.freeze({
    canvas: "#2D2D2D",
    lightForeground: "#FFFFFF",
    darkForeground: "#050506",
    selectionSeedWeight: 0.58,
    surfaceSeedWeight: 0.38,
    subtleSeedWeight: 0.16,
    identitySurfaceSeedWeight: 0.22,
  }),
});

// These are intentionally curated instead of generated from evenly spaced HSL
// values. Each seed keeps its character after the V1 lightness-only contrast
// correction and produces safe local card roles in both Unit themes.
const UNIT_RAINBOW_SEEDS = Object.freeze([
  "#745FF2", // violet
  "#4D64D8", // indigo
  "#2176C7", // blue
  "#008493", // cyan
  "#00836C", // teal
  "#3F7C3A", // green
  "#8A7100", // gold
  "#B55A19", // orange
  "#BF414A", // red
  "#B33C79", // rose
  "#914EB6", // purple
]);

const CONTRAST_MANIFEST = deepFreeze([
  {
    id: "accent-primary-text",
    foregroundRole: "text-on-accent",
    backgroundRole: "accent-primary",
    minimum: 4.5,
    requirement: "normal-text",
  },
  {
    id: "accent-selection-text",
    foregroundRole: "text-on-accent-selection",
    backgroundRole: "accent-selection",
    minimum: 4.5,
    requirement: "normal-text",
  },
  {
    id: "accent-surface-text",
    foregroundRole: "text-on-accent-subtle",
    backgroundRole: "accent-surface",
    minimum: 4.5,
    requirement: "normal-text",
  },
  {
    id: "accent-surface-subtle-text",
    foregroundRole: "text-on-accent-subtle",
    backgroundRole: "accent-surface-subtle",
    minimum: 4.5,
    requirement: "normal-text",
  },
  {
    id: "accent-primary-control-boundary",
    foregroundRole: "accent-primary",
    backgroundRole: "surface-canvas",
    minimum: 3,
    requirement: "control-visual",
  },
  {
    id: "accent-focus-ring",
    foregroundRole: "accent-emphasis",
    backgroundRole: "surface-canvas",
    minimum: 3,
    requirement: "focus-indicator",
    perimeterPx: 2,
  },
  {
    id: "org-identity-card-text",
    foregroundRole: "org-identity-on-surface",
    backgroundRole: "org-identity-surface",
    minimum: 4.5,
    requirement: "normal-text",
  },
  {
    id: "org-identity-accent-boundary",
    foregroundRole: "org-identity-accent",
    backgroundRole: "org-identity-surface",
    minimum: 3,
    requirement: "control-visual",
  },
]);

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }

  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function normalizeAccentSeed(value) {
  if (typeof value !== "string") {
    return null;
  }

  const match = value.trim().match(/^#?([0-9a-fA-F]{6})$/);
  return match ? `#${match[1].toUpperCase()}` : null;
}

function assertTheme(theme) {
  if (!Object.prototype.hasOwnProperty.call(THEME_FOUNDATIONS, theme)) {
    throw new TypeError(`Unsupported appearance theme: ${String(theme)}`);
  }
}

function assertPaletteVersion(paletteVersion) {
  if (paletteVersion !== PALETTE_VERSION) {
    throw new TypeError(
      `Unsupported palette version: ${String(paletteVersion)}`,
    );
  }
}

function hexToRgb(value) {
  const normalized = normalizeAccentSeed(value);
  if (!normalized) {
    throw new TypeError(`Invalid colour: ${String(value)}`);
  }

  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
}

function componentToHex(value) {
  return Math.round(clamp(value, 0, 255))
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();
}

function rgbToHex({ r, g, b }) {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

function rgbToHsl({ r, g, b }) {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const maximum = Math.max(red, green, blue);
  const minimum = Math.min(red, green, blue);
  const delta = maximum - minimum;
  let hue = 0;
  let saturation = 0;
  const lightness = (maximum + minimum) / 2;

  if (delta !== 0) {
    saturation =
      lightness > 0.5
        ? delta / (2 - maximum - minimum)
        : delta / (maximum + minimum);

    if (maximum === red) {
      hue = (green - blue) / delta + (green < blue ? 6 : 0);
    } else if (maximum === green) {
      hue = (blue - red) / delta + 2;
    } else {
      hue = (red - green) / delta + 4;
    }

    hue /= 6;
  }

  return { h: hue, s: saturation, l: lightness };
}

function hueToRgb(p, q, hue) {
  let normalizedHue = hue;
  if (normalizedHue < 0) normalizedHue += 1;
  if (normalizedHue > 1) normalizedHue -= 1;
  if (normalizedHue < 1 / 6) return p + (q - p) * 6 * normalizedHue;
  if (normalizedHue < 1 / 2) return q;
  if (normalizedHue < 2 / 3) {
    return p + (q - p) * (2 / 3 - normalizedHue) * 6;
  }
  return p;
}

function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const channel = l * 255;
    return { r: channel, g: channel, b: channel };
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hueToRgb(p, q, h + 1 / 3) * 255,
    g: hueToRgb(p, q, h) * 255,
    b: hueToRgb(p, q, h - 1 / 3) * 255,
  };
}

function linearChannel(channel) {
  const normalized = channel / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(value) {
  const { r, g, b } = hexToRgb(value);
  return (
    0.2126 * linearChannel(r) +
    0.7152 * linearChannel(g) +
    0.0722 * linearChannel(b)
  );
}

function contrastRatio(first, second) {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

function mixLinear(foreground, background, foregroundWeight) {
  const foregroundRgb = hexToRgb(foreground);
  const backgroundRgb = hexToRgb(background);
  const weight = clamp(foregroundWeight, 0, 1);
  return rgbToHex({
    r: foregroundRgb.r * weight + backgroundRgb.r * (1 - weight),
    g: foregroundRgb.g * weight + backgroundRgb.g * (1 - weight),
    b: foregroundRgb.b * weight + backgroundRgb.b * (1 - weight),
  });
}

function selectReadableForeground(background, foundation) {
  const lightRatio = contrastRatio(foundation.lightForeground, background);
  const darkRatio = contrastRatio(foundation.darkForeground, background);
  return lightRatio >= darkRatio
    ? { colour: foundation.lightForeground, ratio: lightRatio }
    : { colour: foundation.darkForeground, ratio: darkRatio };
}

function lightnessCandidates(origin) {
  const candidates = [];
  for (let index = 0; index <= 500; index += 1) {
    const lightness = index / 500;
    candidates.push({
      lightness,
      distance: Math.abs(lightness - origin),
    });
  }

  return candidates.sort((first, second) => {
    if (first.distance !== second.distance) {
      return first.distance - second.distance;
    }
    return first.lightness - second.lightness;
  });
}

function closestLightnessColour(value, predicate, score) {
  const originalHsl = rgbToHsl(hexToRgb(value));
  let best = null;

  for (const candidate of lightnessCandidates(originalHsl.l)) {
    const colour = rgbToHex(
      hslToRgb({
        h: originalHsl.h,
        s: originalHsl.s,
        l: candidate.lightness,
      }),
    );
    if (!predicate(colour)) continue;

    const candidateScore = typeof score === "function" ? score(colour) : 0;
    if (
      !best ||
      candidate.distance < best.distance ||
      (candidate.distance === best.distance && candidateScore > best.score)
    ) {
      best = { colour, distance: candidate.distance, score: candidateScore };
    }
  }

  return best ? best.colour : value;
}

function ensureContrast(value, background, minimum) {
  if (contrastRatio(value, background) >= minimum) {
    return value;
  }

  return closestLightnessColour(
    value,
    (candidate) => contrastRatio(candidate, background) >= minimum,
    (candidate) => contrastRatio(candidate, background),
  );
}

function ensurePrimary(value, foundation) {
  const hasRequiredContrast = (candidate) => {
    const foreground = selectReadableForeground(candidate, foundation);
    return (
      foreground.ratio >= 4.5 &&
      contrastRatio(candidate, foundation.canvas) >= 3
    );
  };

  return closestLightnessColour(
    value,
    hasRequiredContrast,
    (candidate) => {
      const foreground = selectReadableForeground(candidate, foundation);
      return (
        Math.min(foreground.ratio / 4.5, contrastRatio(candidate, foundation.canvas) / 3)
      );
    },
  );
}

function deriveEmphasis(primary, foundation) {
  const primaryHsl = rgbToHsl(hexToRgb(primary));
  const lightExtremeRatio = contrastRatio(
    foundation.lightForeground,
    foundation.canvas,
  );
  const darkExtremeRatio = contrastRatio(
    foundation.darkForeground,
    foundation.canvas,
  );
  const targetLightness =
    lightExtremeRatio > darkExtremeRatio
      ? Math.min(1, primaryHsl.l + 0.14)
      : Math.max(0, primaryHsl.l - 0.14);
  const candidate = rgbToHex(
    hslToRgb({ ...primaryHsl, l: targetLightness }),
  );
  return ensureContrast(candidate, foundation.canvas, 3);
}

function ensureReadableSurface(surface, foundation) {
  const foreground = selectReadableForeground(surface, foundation);
  if (foreground.ratio >= 4.5) {
    return { surface, foreground: foreground.colour };
  }

  const correctedSurface = closestLightnessColour(
    surface,
    (candidate) => selectReadableForeground(candidate, foundation).ratio >= 4.5,
    (candidate) => selectReadableForeground(candidate, foundation).ratio,
  );
  return {
    surface: correctedSurface,
    foreground: selectReadableForeground(correctedSurface, foundation).colour,
  };
}

function copyRoles(roles) {
  return Object.fromEntries(Object.entries(roles));
}

function generateCustomAccentRoles(accentSeed, theme) {
  const foundation = THEME_FOUNDATIONS[theme];
  const primary = ensurePrimary(accentSeed, foundation);
  const primaryForeground = selectReadableForeground(primary, foundation).colour;
  const emphasis = deriveEmphasis(primary, foundation);

  const rawSelection = mixLinear(
    accentSeed,
    foundation.canvas,
    foundation.selectionSeedWeight,
  );
  const selection = ensureReadableSurface(rawSelection, foundation);

  const rawSurface = mixLinear(
    accentSeed,
    foundation.canvas,
    foundation.surfaceSeedWeight,
  );
  const surface = ensureReadableSurface(rawSurface, foundation);

  const rawSubtleSurface = mixLinear(
    accentSeed,
    foundation.canvas,
    foundation.subtleSeedWeight,
  );
  const subtleSurface = ensureReadableSurface(rawSubtleSurface, foundation);

  // One foreground role intentionally covers both tonal surfaces. Pick the
  // light/dark foreground that maximises the weaker of the two real pairs.
  const candidateForegrounds = [
    foundation.lightForeground,
    foundation.darkForeground,
  ];
  const subtleForeground = candidateForegrounds.sort((first, second) => {
    const firstMinimum = Math.min(
      contrastRatio(first, surface.surface),
      contrastRatio(first, subtleSurface.surface),
    );
    const secondMinimum = Math.min(
      contrastRatio(second, surface.surface),
      contrastRatio(second, subtleSurface.surface),
    );
    return secondMinimum - firstMinimum;
  })[0];
  const sharedSurface = ensureContrast(
    surface.surface,
    subtleForeground,
    4.5,
  );
  const sharedSubtleSurface = ensureContrast(
    subtleSurface.surface,
    subtleForeground,
    4.5,
  );

  return {
    "accent-primary": primary,
    "accent-emphasis": emphasis,
    "accent-selection": selection.surface,
    "accent-surface": sharedSurface,
    "accent-surface-subtle": sharedSubtleSurface,
    "text-on-accent": primaryForeground,
    "text-on-accent-selection": selection.foreground,
    "text-on-accent-subtle": subtleForeground,
  };
}

function generateAccentRoles(
  accentSeed,
  theme = "light",
  options = {},
) {
  assertTheme(theme);
  const paletteVersion = options.paletteVersion ?? PALETTE_VERSION;
  assertPaletteVersion(paletteVersion);
  const normalized = normalizeAccentSeed(accentSeed);

  if (!normalized) {
    return copyRoles(UNIT_PRESET.themes[theme].accentRoles);
  }

  return generateCustomAccentRoles(normalized, theme);
}

function generateOrgIdentityRoles(
  accentSeed,
  theme = "light",
  options = {},
) {
  assertTheme(theme);
  const paletteVersion = options.paletteVersion ?? PALETTE_VERSION;
  assertPaletteVersion(paletteVersion);
  const foundation = THEME_FOUNDATIONS[theme];
  const normalized = normalizeAccentSeed(accentSeed) || UNIT_ACCENT_SEED;
  const rawSurface = mixLinear(
    normalized,
    foundation.canvas,
    foundation.identitySurfaceSeedWeight,
  );
  const readableSurface = ensureReadableSurface(rawSurface, foundation);
  const accent = ensureContrast(normalized, readableSurface.surface, 3);

  return {
    "org-identity-accent": accent,
    "org-identity-surface": readableSurface.surface,
    "org-identity-on-surface": readableSurface.foreground,
  };
}

function hashOrganisationId(orgId) {
  const input = typeof orgId === "string" ? orgId.trim() : "";
  // FNV-1a, forced to uint32 after each multiplication for identical browser
  // and Node results.
  let hash = 0x811c9dc5;
  const value = input || "unit-organisation";
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function getUnitRainbowSeed(orgId) {
  const index = hashOrganisationId(orgId) % UNIT_RAINBOW_SEEDS.length;
  return UNIT_RAINBOW_SEEDS[index];
}

function resolveOrgIdentitySeed(orgId, accentSeed) {
  return normalizeAccentSeed(accentSeed) || getUnitRainbowSeed(orgId);
}

function resolveManifestColour(role, accentRoles, orgIdentityRoles, theme) {
  if (role === "surface-canvas") {
    return THEME_FOUNDATIONS[theme].canvas;
  }
  return accentRoles[role] || orgIdentityRoles[role] || null;
}

function evaluateContrastManifest({
  accentRoles,
  orgIdentityRoles,
  theme = "light",
} = {}) {
  assertTheme(theme);
  if (!accentRoles || !orgIdentityRoles) {
    throw new TypeError(
      "accentRoles and orgIdentityRoles are required to evaluate contrast",
    );
  }

  return CONTRAST_MANIFEST.map((entry) => {
    const foreground = resolveManifestColour(
      entry.foregroundRole,
      accentRoles,
      orgIdentityRoles,
      theme,
    );
    const background = resolveManifestColour(
      entry.backgroundRole,
      accentRoles,
      orgIdentityRoles,
      theme,
    );
    if (!foreground || !background) {
      throw new TypeError(
        `Missing colour role for contrast pair ${entry.id}`,
      );
    }
    const ratio = contrastRatio(foreground, background);
    return {
      ...entry,
      foreground,
      background,
      ratio: Number(ratio.toFixed(2)),
      pass: ratio + Number.EPSILON >= entry.minimum,
    };
  });
}

function generateAppearancePalette(accentSeed, options = {}) {
  const paletteVersion = options.paletteVersion ?? PALETTE_VERSION;
  assertPaletteVersion(paletteVersion);
  const normalized = normalizeAccentSeed(accentSeed);
  const resolvedSeed = normalized || UNIT_ACCENT_SEED;
  const usedFallback = !normalized;
  const themes = {};

  for (const theme of Object.keys(THEME_FOUNDATIONS)) {
    const accentRoles = usedFallback
      ? copyRoles(UNIT_PRESET.themes[theme].accentRoles)
      : generateCustomAccentRoles(resolvedSeed, theme);
    const orgIdentityRoles = generateOrgIdentityRoles(
      resolvedSeed,
      theme,
      { paletteVersion },
    );
    themes[theme] = {
      accentRoles,
      orgIdentityRoles,
      contrast: evaluateContrastManifest({
        accentRoles,
        orgIdentityRoles,
        theme,
      }),
    };
  }

  return {
    schemaVersion: APPEARANCE_SCHEMA_VERSION,
    paletteVersion,
    accentSeed: resolvedSeed,
    usedFallback,
    themes,
  };
}

// Integration-oriented shape used by the Web runtime. The granular functions
// remain public for Functions validation and focused tests.
function generateAppearance(accentSeed, options = {}) {
  const palette = generateAppearancePalette(accentSeed, options);
  const themes = {};
  const manifest = {};

  for (const theme of Object.keys(THEME_FOUNDATIONS)) {
    const generatedTheme = palette.themes[theme];
    themes[theme] = {
      tokens: copyRoles(generatedTheme.accentRoles),
      orgIdentity: {
        accent: generatedTheme.orgIdentityRoles["org-identity-accent"],
        surface: generatedTheme.orgIdentityRoles["org-identity-surface"],
        onSurface:
          generatedTheme.orgIdentityRoles["org-identity-on-surface"],
      },
    };
    manifest[theme] = generatedTheme.contrast.map(
      ({ minimum, ...pair }) => ({
        ...pair,
        threshold: minimum,
      }),
    );
  }

  return {
    paletteVersion: palette.paletteVersion,
    accentSeed: palette.accentSeed,
    usedFallback: palette.usedFallback,
    themes,
    manifest,
  };
}

function generateOrgIdentityPalette({ orgId, accentSeed, paletteVersion = 1 } = {}) {
  assertPaletteVersion(paletteVersion);
  const normalized = normalizeAccentSeed(accentSeed);
  const resolvedSeed = normalized || getUnitRainbowSeed(orgId);
  const source = normalized ? "appearance" : "unit-rainbow";

  return {
    paletteVersion,
    accentSeed: resolvedSeed,
    source,
    themes: {
      light: generateOrgIdentityRoles(resolvedSeed, "light", {
        paletteVersion,
      }),
      dark: generateOrgIdentityRoles(resolvedSeed, "dark", {
        paletteVersion,
      }),
    },
  };
}

const UNIT_PRESET = deepFreeze({
  schemaVersion: APPEARANCE_SCHEMA_VERSION,
  paletteVersion: PALETTE_VERSION,
  accentSeed: UNIT_ACCENT_SEED,
  themes: {
    light: {
      accentRoles: {
        "accent-primary": "#745FF2",
        "accent-emphasis": "#513FBF",
        "accent-selection": "#B5AAFF",
        "accent-surface": "#D2CBFB",
        "accent-surface-subtle": "#DBD5FF",
        "text-on-accent": "#FFFFFF",
        "text-on-accent-selection": "#232425",
        "text-on-accent-subtle": "#050506",
      },
      orgIdentityRoles: generateOrgIdentityRoles(UNIT_ACCENT_SEED, "light"),
    },
    dark: {
      accentRoles: {
        "accent-primary": "#B5AAFF",
        "accent-emphasis": "#D2CBFB",
        "accent-selection": "#745FF2",
        "accent-surface": "#513FBF",
        "accent-surface-subtle": "#050506",
        "text-on-accent": "#232425",
        "text-on-accent-selection": "#FFFFFF",
        "text-on-accent-subtle": "#DBD5FF",
      },
      orgIdentityRoles: generateOrgIdentityRoles(UNIT_ACCENT_SEED, "dark"),
    },
  },
});

module.exports = {
  ACCENT_ROLE_NAMES,
  APPEARANCE_SCHEMA_VERSION,
  CONTRAST_MANIFEST,
  ORG_IDENTITY_ROLE_NAMES,
  PALETTE_VERSION,
  THEME_FOUNDATIONS,
  UNIT_ACCENT_SEED,
  UNIT_PRESET,
  UNIT_RAINBOW_SEEDS,
  contrastRatio,
  evaluateContrastManifest,
  generateAccentRoles,
  generateAppearance,
  generateAppearancePalette,
  generateOrgIdentityPalette,
  generateOrgIdentityRoles,
  getUnitRainbowSeed,
  normalizeAccentSeed,
  resolveOrgIdentitySeed,
  unitRainbowSeed: getUnitRainbowSeed,
};
